import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// Bullet keys for i18n lookup
const BULLET_KEYS = ['noCreditCard', 'startFree', 'allFeatures', 'builtForDealers'] as const;

interface SignupHeroSectionProps {
  showHero: boolean;
  reducedMotion: boolean;
}

export function SignupHeroSection({ showHero, reducedMotion }: SignupHeroSectionProps) {
  const { t } = useTranslation('common');

  return (
    <div
      className={cn(
        // Only animate opacity - vertical positioning is handled by parent wrapper
        'transition-opacity duration-500 ease-out',
        reducedMotion && 'transition-none',
        showHero ? 'opacity-100' : 'opacity-0'
      )}
      aria-hidden={!showHero}
    >
      {/* Headline - larger size for more dominant appearance */}
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
        {t('auth.signUp.hero.headline')}
      </h1>

      {/* Reassurance bullets - 2x2 grid on desktop, single column on mobile */}
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {BULLET_KEYS.map((key, index) => (
          <li
            key={key}
            className={cn(
              'flex items-start gap-2 transition-all duration-300 ease-out min-w-0',
              reducedMotion && 'transition-none',
              // Stagger bullet appearance
              showHero ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
            )}
            style={{
              transitionDelay: reducedMotion ? '0ms' : `${index * 75}ms`,
            }}
          >
            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm text-muted-foreground min-w-0 break-words">
              {t(`auth.signUp.hero.bullets.${key}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
